import { useTheme } from '@/hooks/useTheme';
import { useTranslation } from 'react-i18next';
import { OwnerStation, ownerStatusColor, formatMoney, formatMoneyShort, shortStationName } from '@/mocks/ownerDashboard';
import { OwnerKpis } from '@/hooks/useOwnerDashboard';

interface Props {
  kpis: OwnerKpis | null;
  stations: OwnerStation[];
  loading?: boolean;
  /** Revenue tiles (month + today) -> per-SES financial page. */
  onStationFinancial?: (s: OwnerStation) => void;
  /** Technical-status tile -> per-SES asset detail (technical tab). */
  onStationDetail?: (s: OwnerStation) => void;
  /** Forecast tile -> /forecast with that SES selected in the topbar. */
  onStationForecast?: (s: OwnerStation) => void;
}

export default function HeroKpiRow({
  kpis,
  stations,
  loading,
  onStationFinancial,
  onStationDetail,
  onStationForecast,
}: Props) {
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const cardBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = '#6B6B70';
  const textDim = isDark ? '#4A4A50' : '#9B9BA4';
  const rowBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F0F0F2';
  const hoverBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';

  const dash = loading ? '…' : '—';
  const workingCount = kpis?.workingCount ?? stations.filter((s) => s.status === 'green' || s.status === 'amber').length;
  const totalCount = kpis?.totalCount ?? stations.length;
  const fmtMwh = (mwh: number | null | undefined) =>
    mwh == null ? dash : `${mwh.toFixed(1)} ${t('d_u_mwh')}`;

  // Single canonical row order shared by ALL four tiles, so each station sits on
  // the SAME row across "Дохід…", "Технічний стан" and "Прогноз…" and the columns
  // line up horizontally. Pre-launch sinks to the bottom; the rest by capacity desc.
  const orderedStations = [...stations].sort((a, b) => {
    const ap = a.status === 'pre_launch' ? 1 : 0;
    const bp = b.status === 'pre_launch' ? 1 : 0;
    if (ap !== bp) return ap - bp;
    return (b.installedMw ?? 0) - (a.installedMw ?? 0);
  });

  // ----- per-station breakdown row list (money / energy) --------------------
  type Metric = { get: (s: OwnerStation) => number | null | undefined; fmt: (v: number) => string };
  const breakdown = (m: Metric, onRowClick?: (s: OwnerStation) => void) => {
    const rows = orderedStations.map((s) => ({ s, v: m.get(s) }));
    const clickable = !!onRowClick;
    return (
      <div className="mt-3 pt-2.5 space-y-0.5" style={{ borderTop: `1px solid ${cardBorder}` }}>
        {rows.map(({ s, v }) => (
          <div key={s.id}
               onClick={clickable ? () => onRowClick!(s) : undefined}
               role={clickable ? 'button' : undefined}
               tabIndex={clickable ? 0 : undefined}
               className={`flex items-center justify-between gap-2 py-0.5 rounded ${clickable ? 'cursor-pointer -mx-1.5 px-1.5' : ''}`}
               style={{ borderBottom: `1px solid ${rowBorder}` }}
               onMouseEnter={clickable ? (e) => { e.currentTarget.style.background = hoverBg; } : undefined}
               onMouseLeave={clickable ? (e) => { e.currentTarget.style.background = 'transparent'; } : undefined}>
            <span className="text-[11px] truncate flex items-center gap-1.5" style={{ color: textMuted }}>
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: ownerStatusColor(s.status) }} />
              {shortStationName(s.name)}
            </span>
            <span className="text-[11px] font-semibold tabular-nums shrink-0 flex items-center gap-1"
                  style={{ color: s.status === 'pre_launch' ? textDim : textMain }}>
              {s.status === 'pre_launch' || v == null ? '—' : m.fmt(v)}
              {clickable && <i className="ri-arrow-right-s-line" style={{ color: textDim, fontSize: 13 }} />}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const Tile = ({ children }: { children: React.ReactNode }) => (
    <div className="rounded-xl p-4 flex flex-col h-full" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      {children}
    </div>
  );

  const Head = ({ label, icon, accent, right }: { label: string; icon?: string; accent?: string; right?: React.ReactNode }) => (
    <div className="flex items-start justify-between mb-2.5">
      <div className="text-[10px] font-bold tracking-widest uppercase" style={{ color: textMuted, fontFamily: 'Muller, Inter, sans-serif' }}>
        {label}
      </div>
      {right ? right : icon ? (
        <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ background: `${accent}15` }}>
          <i className={icon} style={{ color: accent, fontSize: 14 }} />
        </div>
      ) : null}
    </div>
  );

  const Value = ({ text, color }: { text: string; color: string }) => (
    <span className="text-base font-bold tabular-nums leading-tight block"
          style={{ color, letterSpacing: '-0.02em', fontFamily: 'Muller, Inter, sans-serif' }}>{text}</span>
  );

  const Note = ({ text, color }: { text: string; color?: string }) => (
    <div className="text-[11px] mt-0.5" style={{ color: color || textMuted, fontFamily: 'Muller, Inter, sans-serif' }}>{text}</div>
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5 items-stretch">

      {/* 1 — Earned this month */}
      <Tile>
        <Head label={t('d_kpi_month_rev')} icon="ri-money-dollar-circle-line" accent="#E0A030" />
        <Value text={kpis ? formatMoney(kpis.earnedMonthUah) : dash} color="#E0A030" />
        <Note text={t('d_kpi_guaranteed')} />
        <div className="text-[10px] mt-0.5" style={{ color: textDim }}>{t('d_kpi_contract_rdn')}</div>
        {kpis && breakdown({ get: (s) => s.monthRevenueUah, fmt: (v) => `${formatMoneyShort(v)} ${t('d_u_uah')}` }, onStationFinancial)}
      </Tile>

      {/* 2 — Earned today (guaranteed, day-ahead) */}
      <Tile>
        <Head label={t('d_kpi_today_rev')} icon="ri-money-dollar-circle-line" accent="#0D7A3C" />
        <Value text={kpis ? formatMoney(kpis.forecastTodayRevenueUah) : dash} color="#0D7A3C" />
        <Note text={t('d_kpi_guaranteed')} />
        <div className="text-[10px] mt-0.5" style={{ color: textDim }}>{t('d_kpi_sold_da')}</div>
        {kpis && breakdown({ get: (s) => s.todayRevenueUah, fmt: (v) => `${formatMoneyShort(v)} ${t('d_u_uah')}` }, onStationFinancial)}
      </Tile>

      {/* 3 — Portfolio status (list) */}
      <Tile>
        <Head label={t('d_kpi_tech_status')} icon="ri-pulse-line" accent="#0D7A3C" />
        <Value text={`${workingCount}/${totalCount} ${t('d_kpi_generating')}`} color={workingCount === totalCount ? '#22C55E' : textMain} />
        <Note text={t('d_kpi_current_status')} />
        {/* caption line — keeps the 3-line header height identical to the revenue/
            forecast tiles so the station rows line up across all four tiles */}
        <div className="text-[10px] mt-0.5" style={{ color: textDim }}>{t('d_kpi_updates_auto')}</div>
        <div className="mt-3 pt-2.5 space-y-0.5 flex-1" style={{ borderTop: `1px solid ${cardBorder}` }}>
          {orderedStations.map((s) => {
            const clickable = !!onStationDetail;
            return (
            <div key={s.id}
                 onClick={clickable ? () => onStationDetail!(s) : undefined}
                 role={clickable ? 'button' : undefined}
                 tabIndex={clickable ? 0 : undefined}
                 className={`flex items-center justify-between gap-2 py-0.5 rounded ${clickable ? 'cursor-pointer -mx-1.5 px-1.5' : ''}`}
                 style={{ borderBottom: `1px solid ${rowBorder}` }}
                 onMouseEnter={clickable ? (e) => { e.currentTarget.style.background = hoverBg; } : undefined}
                 onMouseLeave={clickable ? (e) => { e.currentTarget.style.background = 'transparent'; } : undefined}>
              <span className="text-[11px] truncate flex items-center gap-1.5" style={{ color: textMain }}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: ownerStatusColor(s.status) }} />
                {shortStationName(s.name)}
              </span>
              <span className="text-[10px] font-medium shrink-0 text-right flex items-center gap-1" style={{ color: ownerStatusColor(s.status) }}>
                {s.statusLabel}
                {clickable && <i className="ri-arrow-right-s-line" style={{ color: textDim, fontSize: 13 }} />}
              </span>
            </div>
          );})}
        </div>
      </Tile>

      {/* 4 — Forecast today (MWh) */}
      <Tile>
        <Head label={t('d_kpi_forecast_today')} icon="ri-sun-line" accent="#1F5B8F" />
        <Value text={fmtMwh(kpis?.forecastTodayMwh)} color="#1F5B8F" />
        <Note text={`${t('d_kpi_fact_now')}: ${kpis?.factNowMw != null ? `${kpis.factNowMw.toFixed(2)} ${t('d_u_mw')}` : dash}`} />
        <div className="text-[10px] mt-0.5" style={{ color: textDim }}>{t('d_kpi_expected_gen')}</div>
        {kpis && breakdown({ get: (s) => s.todayForecastMwh, fmt: (v) => `${v.toFixed(1)}` }, onStationForecast)}
      </Tile>

    </div>
  );
}
